import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatError } from "@/api/client";
import { Plus, FolderOpen, FileText, Trash2, Pencil, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import IconPicker from "@/components/IconPicker";

export default function ContentTab({ space, onReload }) {
  const { spaceId } = useParams();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [showNewCat, setShowNewCat] = useState(false);
  const [showNewArt, setShowNewArt] = useState(false);
  const [editingCat, setEditingCat] = useState(null);

  const loadCats = async () => {
    const { data } = await api.get(`/spaces/${spaceId}/categories`);
    setCategories(data);
    if (!activeCat && data.length) setActiveCat(data[0]);
  };
  const loadArts = async (catId) => {
    if (!catId) return setArticles([]);
    const { data } = await api.get(`/spaces/${spaceId}/articles?category_id=${catId}`);
    setArticles(data);
  };

  useEffect(() => { loadCats(); }, [spaceId]);
  useEffect(() => { if (activeCat) loadArts(activeCat.id); }, [activeCat]);

  const onDeleteCat = async (id) => {
    if (!confirm("Delete this category? Articles will be moved to Uncategorized.")) return;
    try {
      await api.delete(`/spaces/${spaceId}/categories/${id}`);
      toast.success("Category deleted");
      await loadCats();
      setActiveCat(null);
    } catch (e) { toast.error(formatError(e)); }
  };

  const onDeleteArt = async (id) => {
    if (!confirm("Delete this article?")) return;
    try {
      await api.delete(`/spaces/${spaceId}/articles/${id}`);
      toast.success("Article deleted");
      loadArts(activeCat.id);
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="flex h-[calc(100vh-128px)]">
      <aside className="w-72 bg-white border-r border-zinc-200 flex flex-col">
        <div className="p-4 flex items-center justify-between border-b border-zinc-200">
          <h3 className="font-bold tracking-tight text-sm uppercase text-zinc-500">Categories</h3>
          <button onClick={() => setShowNewCat(true)} data-testid="new-category-btn"
            className="p-1.5 text-zinc-500 hover:bg-zinc-100 rounded-sm">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {categories.length === 0 && (
            <div className="text-xs text-zinc-500 p-3">No categories. Create your first topic.</div>
          )}
          {(() => {
            const top = categories.filter((c) => !c.parent_id);
            const subsByParent = categories.reduce((acc, c) => {
              if (c.parent_id) (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
              return acc;
            }, {});
            const rows = [];
            top.forEach((t) => {
              rows.push({ ...t, depth: 0 });
              (subsByParent[t.id] || []).forEach((s) => rows.push({ ...s, depth: 1 }));
            });
            // Orphan sub-categories (no matching parent) — show at top level
            categories.filter((c) => c.parent_id && !top.find((t) => t.id === c.parent_id))
              .forEach((c) => rows.push({ ...c, depth: 0 }));
            return rows.map((c) => {
              const arName = ((c.i18n || {}).ar || {}).name;
              const arMissing = !arName || !String(arName).trim();
              return (
              <button key={c.id} onClick={() => setActiveCat(c)}
                data-testid={`category-${c.slug}`}
                style={{ paddingLeft: c.depth ? "1.75rem" : undefined }}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-sm group transition-colors ${
                  activeCat?.id === c.id ? "bg-zinc-100 font-semibold" : "hover:bg-zinc-50"
                }`}>
                {c.depth > 0 && <span className="text-zinc-300 text-xs">└</span>}
                <span className="text-base">{c.icon || "📁"}</span>
                <span className="flex-1 text-left truncate">{c.name}</span>
                {arMissing && (
                  <span title="No Arabic translation yet — click the pencil to add it"
                    data-testid={`ar-missing-${c.slug}`}
                    className="text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200">
                    AR?
                  </span>
                )}
                {c.depth === 0 && (
                  <span className="text-[9px] uppercase tracking-wider font-bold text-[#41B5E6] opacity-60">TOPIC</span>
                )}
                <span className="text-xs text-zinc-400 opacity-0 group-hover:opacity-100"
                  role="button" onClick={(e) => { e.stopPropagation(); setEditingCat(c); }}>
                  <Pencil className="w-3 h-3" />
                </span>
                <span className="text-xs text-zinc-400 opacity-0 group-hover:opacity-100"
                  role="button" onClick={(e) => { e.stopPropagation(); onDeleteCat(c.id); }}>
                  <Trash2 className="w-3 h-3 hover:text-rose-600" />
                </span>
              </button>
              );
            });
          })()}
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto p-6">
        {!activeCat ? (
          <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
            Pick or create a category to start writing
          </div>
        ) : (
          <div className="max-w-4xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1 font-bold">Category</div>
                <h2 className="text-2xl font-black tracking-tight flex items-center gap-2">
                  <span>{activeCat.icon}</span> {activeCat.name}
                </h2>
                {activeCat.description && <p className="text-sm text-zinc-500 mt-1">{activeCat.description}</p>}
              </div>
              <button onClick={() => setShowNewArt(true)}
                data-testid="new-article-btn"
                className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Article
              </button>
            </div>

            <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100">
              {articles.length === 0 ? (
                <div className="p-12 text-center text-sm text-zinc-500">
                  No articles in this category yet.
                </div>
              ) : articles.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-4 hover:bg-zinc-50">
                  <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                  <Link to={`/spaces/${spaceId}/articles/${a.id}`} className="flex-1 min-w-0"
                    data-testid={`article-link-${a.slug}`}>
                    <div className="font-semibold text-sm truncate">{a.title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">{a.excerpt || "No excerpt"}</div>
                  </Link>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${
                    a.status === "published" ? "bg-emerald-100 text-emerald-800" :
                    a.status === "draft" ? "bg-zinc-100 text-zinc-700" : "bg-amber-100 text-amber-800"
                  }`}>{a.status}</span>
                  <button onClick={() => onDeleteArt(a.id)} className="text-zinc-400 hover:text-rose-600"
                    data-testid={`delete-article-${a.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {showNewCat && <CategoryModal spaceId={spaceId} categories={categories} onClose={() => setShowNewCat(false)} onSaved={loadCats} />}
      {editingCat && <CategoryModal spaceId={spaceId} categories={categories} initial={editingCat} onClose={() => setEditingCat(null)} onSaved={loadCats} />}
      {showNewArt && activeCat && (
        <ArticleQuickModal spaceId={spaceId} categoryId={activeCat.id}
          onClose={() => setShowNewArt(false)}
          onSaved={() => loadArts(activeCat.id)} />
      )}
    </div>
  );
}

function CategoryModal({ spaceId, initial, categories = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    icon: initial?.icon || "📁",
    description: initial?.description || "",
    status: initial?.status || "published",
    parent_id: initial?.parent_id || null,
  });
  const initialI18n = (initial?.i18n || {}).ar || {};
  const [nameAr, setNameAr] = useState(initialI18n.name || "");
  const [descAr, setDescAr] = useState(initialI18n.description || "");
  const [activeLang, setActiveLang] = useState("en");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...form,
        i18n: { ar: { name: nameAr, description: descAr } },
      };
      if (initial) {
        await api.put(`/spaces/${spaceId}/categories/${initial.id}`, payload);
      } else {
        await api.post(`/spaces/${spaceId}/categories`, payload);
      }
      toast.success(initial ? "Updated" : "Created");
      onClose();
      onSaved();
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  const copyEnToAr = () => {
    setNameAr(form.name);
    setDescAr(form.description);
    toast.success("Copied from English. Now translate.");
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-sm" onClick={(e) => e.stopPropagation()} data-testid="category-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-bold">{initial ? "Edit category" : "New category"}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-500" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Lang tabs */}
          <div className="flex items-center gap-2 border-b border-zinc-200 pb-2">
            <button type="button" onClick={() => setActiveLang("en")}
              data-testid="cat-lang-en"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm ${
                activeLang === "en" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"
              }`}>🇺🇸 English</button>
            <button type="button" onClick={() => setActiveLang("ar")}
              data-testid="cat-lang-ar"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm ${
                activeLang === "ar" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"
              }`}>🇸🇦 العربية</button>
            {activeLang === "ar" && (
              <button type="button" onClick={copyEnToAr}
                data-testid="cat-copy-en-to-ar"
                className="ml-auto text-xs font-semibold text-[#41B5E6] hover:underline">
                ⤴ Copy from English
              </button>
            )}
          </div>

          {activeLang === "en" ? (
            <>
              <div>
                <label className="block text-sm font-bold mb-1.5">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="category-name-input"
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1.5">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
              </div>
            </>
          ) : (
            <div dir="rtl" className="text-right">
              <div className="mb-3">
                <label className="block text-sm font-bold mb-1.5">الاسم (Arabic name)</label>
                <input value={nameAr} onChange={(e) => setNameAr(e.target.value)}
                  data-testid="category-name-ar"
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1.5">الوصف (Arabic description)</label>
                <textarea value={descAr} onChange={(e) => setDescAr(e.target.value)} rows={2}
                  data-testid="category-desc-ar"
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-bold mb-1.5">Parent topic</label>
            <select value={form.parent_id || ""} onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
              data-testid="category-parent-select"
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm">
              <option value="">— None (this is a top-level Topic) —</option>
              {categories.filter((c) => !c.parent_id && c.id !== initial?.id).map((c) => (
                <option key={c.id} value={c.id}>↳ Sub-topic of: {c.icon} {c.name}</option>
              ))}
            </select>
            <p className="text-xs text-zinc-500 mt-1">Topics appear as cards on the home page. Sub-topics appear inside their parent topic.</p>
          </div>
          <div>
            <label className="block text-sm font-bold mb-1.5">Icon</label>
            <IconPicker value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} testId="category-icon-picker" />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1.5">Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          {error && <div className="text-sm text-rose-600">{error}</div>}
          <button type="submit" disabled={loading} data-testid="category-submit"
            className="w-full bg-[#41B5E6] hover:bg-[#2A9BCC] text-white font-semibold py-2.5 rounded-sm disabled:opacity-50">
            {loading ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ArticleQuickModal({ spaceId, categoryId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/spaces/${spaceId}/articles`, { title, category_id: categoryId });
      toast.success("Article created");
      onClose();
      onSaved();
      window.location.href = `/spaces/${spaceId}/articles/${data.id}`;
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-bold">New article</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-500" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-bold mb-1.5">Title</label>
            <input required value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="article-title-input"
              placeholder="How do I…"
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
          </div>
          {error && <div className="text-sm text-rose-600">{error}</div>}
          <button type="submit" disabled={loading || !title} data-testid="article-create-submit"
            className="w-full bg-[#41B5E6] hover:bg-[#2A9BCC] text-white font-semibold py-2.5 rounded-sm disabled:opacity-50">
            {loading ? "Creating…" : "Create & Edit"}
          </button>
        </form>
      </div>
    </div>
  );
}
